import React from "react";
import Application, { mountComponent, openDialog } from "./Application";
import { t } from "./i18n";

async function checkAuth(community) {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const url = `/api/user/status?community=${encodeURIComponent(community)}`;

        const res = await fetch(url, {
            method: "GET",
            credentials: "include",
            headers: {
                "Cache-Control": "no-cache",
                "Accept": "application/json",
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
            console.warn(`Auth check failed: HTTP ${res.status}`);
            return false;
        }

        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.warn("Auth check returned non-JSON response");
            return false;
        }

        const json = await res.json();

        if (json.error) {
            console.warn("Server reported error in status check:", json.message);
            return false;
        }

        return json.authenticated === true;

    } catch (error) {
        if (error.name === "AbortError") {
            console.error("Authentication check timed out");
        } else {
            console.error("Authentication check failed:", error);
        }
        return false;
    }
}


class ProtectedLinkHandler {
    constructor(linkElement, options = {}) {
        this.link = linkElement;
        this.options = {
            title: t("Email:"),
            size: "small",
            url: "url",
            community: "community",
            ...options,
        };
        this.handler = null;
        this.isProcessing = false;
    }

    initialize() {
        if (this.link.dataset.protectedInit === "true") {
            return false;
        }

        const originalUrl = this.link.getAttribute("href");

        if (!originalUrl || originalUrl === "#" || originalUrl === "") {
            console.warn("Protected link has no valid href:", this.link);
            return false;
        }

        this.link.setAttribute(this.options.url, originalUrl);
        this.link.setAttribute("href", "#");
        this.link.setAttribute("protected", "true");
        this.link.classList.add("protected");
        this.link.dataset.protectedInit = "true";

        this.handler = this.createClickHandler();
        this.link.addEventListener("click", this.handler);

        return true;
    }

    createClickHandler() {
        return async (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.isProcessing) return;
            this.isProcessing = true;

            try {
                const url = this.link.getAttribute(this.options.url);
                const community = this.link.getAttribute(this.options.community);

                if (!url || url === "#") {
                    console.error("No valid protected URL found");
                    return;
                }

                const authenticated = await checkAuth(community);

                if (authenticated) {
                    this.redirect(url);
                } else {
                    this.showAuthDialog(url, community);
                }

            } catch (error) {
                console.error("Error in protected link handler:", error);
                const url = this.link.getAttribute(this.options.url);
                if (url && url !== "#") {
                    this.showAuthDialog(url);
                }
            } finally {
                setTimeout(() => {
                    this.isProcessing = false;
                }, 300);
            }
        };
    }

    showAuthDialog(url, community) {
        openDialog(
            "email",
            {
                onSuccess: () => {
                    this.redirect(url);
                },
                onError: (error) => {
                    console.error("Authentication error:", error);
                    this.isProcessing = false;
                },
                community: community
            },
            this.options.title,
            this.options.size
        );
    }

    redirect(url) {
        try {
            const urlObj = new URL(url, window.location.origin);

            if (urlObj.origin === window.location.origin) {
                window.location.href = urlObj.href;
            } else {
                console.warn("Cross-origin redirect blocked:", url);
                window.location.href = url;
            }
        } catch (error) {
            console.error("Invalid URL:", url, error);
        }
    }

    destroy() {
        if (this.handler) {
            this.link.removeEventListener("click", this.handler);
            this.handler = null;
        }
        this.link.removeAttribute("protected");
    }
}

function protectLink(linkElement, options = {}) {
    const handler = new ProtectedLinkHandler(linkElement, options);
    return handler.initialize();
}

function initializeProtection(options = {}) {
    const {
        selector = ".protected",
        title = "Email",
        size = "small",
    } = options;

    const links = document.querySelectorAll(selector);
    if (links.length === 0) return 0;

    let successCount = 0;
    links.forEach((link) => {
        if (protectLink(link, { title, size })) {
            successCount++;
        }
    });

    return successCount;
}

function init() {
    const mounted = mountComponent("root", Application);
    if (!mounted) return false;

    initializeProtection();
    return true;
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}

const Protection = ({
    selector = ".protected",
    title = t("Sign in:"),
    size = "small",
}) => {
    React.useEffect(() => {
        initializeProtection({ selector, title, size });

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches?.(selector)) {
                            protectLink(node, { title, size });
                        }
                        node.querySelectorAll?.(selector)?.forEach((link) => {
                            protectLink(link, { title, size });
                        });
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [selector, title, size]);

    return null;
};

export { initializeProtection, protectLink };
export default Protection;
